import { Table } from "@shared/ui";
import type { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGanttDrag } from "./hooks/useGanttDrag";
import { useGanttTimeline } from "./hooks/useGanttTimeline";
import styles from "./styles.module.scss";
import { GanttChartProps, GanttRowRecord, RangePreset } from "./types";
import { buildGanttColumns } from "./ui/buildGanttColumns";
import { GanttHeader } from "./ui/GanttHeader";
import { addDays, startOfDay, toISODate } from "./utils/dateUtils";
import {
  applyStageDurations,
  getStageStatuses,
  placeStages,
  spreadStagesEvenly,
} from "./utils/stageUtils";

export const GanttChart: React.FC<GanttChartProps> = ({
  creators,
  timelineByDeliverable,
  campaignStartDate,
  campaignEndDate,
  deliverableNames: deliverableNamesProp,
  onOpenDeliverable,
  onUpdateDeliverableTimeline,
  onChangeDeliverableTimeline,
  onApplyTimelineToAll,
  onEqualizeTimelines,
  onApplyStageDurations,
  className,
  emptyText,
  title,
  onDone,
  onCancel,
  isSaving,
  scrollY = 650,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [selectedCreatorIds, setSelectedCreatorIds] = useState<Set<string | number>>(
    () => new Set()
  );

  const deliverableNames = useMemo(() => {
    if (deliverableNamesProp?.length) return deliverableNamesProp;
    const keys = new Set<string>();
    creators.forEach((c) => {
      c.deliverables.forEach((d) => {
        keys.add(`${c.id}_${d.id}`);
        keys.add(d.id);
        keys.add(d.name);
      });
    });
    return Array.from(keys);
  }, [creators, deliverableNamesProp]);

  const allCreatorIds = useMemo(() => creators.map((c) => c.id), [creators]);
  const allCreatorsSelected =
    allCreatorIds.length > 0 &&
    allCreatorIds.every((id) => selectedCreatorIds.has(id));
  const someCreatorsSelected = selectedCreatorIds.size > 0;

  const {
    rangeStart,
    rangeEnd,
    totalDays,
    ticks,
    todayPct,
    rangePresets,
    handleRangeStartChange,
    handleRangeEndChange,
    applyPreset,
    setUserAdjusted,
  } = useGanttTimeline({
    campaignStartDate,
    campaignEndDate,
    deliverableNames,
    timelineByDeliverable,
    onChangeDeliverableTimeline,
    onApplyTimelineToAll,
  });

  const creatorsKey = useMemo(
    () => creators.map((c) => `${c.id}_${c.deliverables.map((d) => d.id).join(",")}`).join("|"),
    [creators]
  );
  const prevCreatorsKeyRef = useRef(creatorsKey);

  useEffect(() => {
    if (prevCreatorsKeyRef.current !== creatorsKey) {
      prevCreatorsKeyRef.current = creatorsKey;
      setEditMode(false);
      setSelectedCreatorIds((prev) => (prev.size === 0 ? prev : new Set()));
      setUserAdjusted(false);
    }
  }, [creatorsKey, setUserAdjusted]);

  const { drag, previewMap, beginDrag, registerTrackRef } = useGanttDrag({
    editMode,
    creators,
    selectedCreatorIds,
    setSelectedCreatorIds,
    timelineByDeliverable,
    rangeStart,
    rangeEnd,
    totalDays,
    onUpdateDeliverableTimeline,
    onChangeDeliverableTimeline,
    setUserAdjusted,
  });

  const activeTimeline = previewMap ?? timelineByDeliverable;

  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>(() =>
    creators.filter((c) => c.deliverables.length > 0).map((c) => `creator-${c.id}`)
  );

  const toggleCreatorSelected = useCallback((id: string | number) => {
    setSelectedCreatorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllCreators = useCallback(() => {
    setSelectedCreatorIds(new Set(allCreatorIds));
  }, [allCreatorIds]);

  const clearCreatorSelection = useCallback(() => {
    setSelectedCreatorIds(new Set());
  }, []);

  const cancelEditMode = useCallback(() => {
    if (isSaving) return;
    onCancel?.();
    setEditMode(false);
    setSelectedCreatorIds(new Set());
    setUserAdjusted(false);
  }, [isSaving, onCancel, setUserAdjusted]);

  const toggleEditMode = () => {
    if (editMode) {
      if (isSaving) return;
      if (onDone) {
        onDone(() => {
          setEditMode(false);
          setSelectedCreatorIds(new Set());
        });
      } else {
        setEditMode(false);
        setSelectedCreatorIds(new Set());
      }
    } else {
      setEditMode(true);
      setSelectedCreatorIds(new Set());
    }
  };

  const handleApplyStageDurations = useCallback(
    (durations: Record<string, number>) => {
      const today = startOfDay(new Date());
      handleRangeStartChange(toISODate(today));
      const totalStageDays =
        (durations.brief || 0) +
        (durations.script || 0) +
        (durations.draft || 0) +
        (durations.live || 0);
      const calculatedEnd = addDays(today, totalStageDays);
      if (rangeEnd < calculatedEnd) {
        handleRangeEndChange(toISODate(addDays(calculatedEnd, 7)));
      }

      setEditMode(true);
      if (onApplyStageDurations) {
        onApplyStageDurations(durations, today);
      } else {
        creators.forEach((c) => {
          c.deliverables.forEach((d) => {
            const deliverableKey = `${c.id}_${d.id}`;
            const existing =
              activeTimeline?.[deliverableKey] ||
              activeTimeline?.[d.id] ||
              (d.stages && d.stages.length > 0
                ? d.stages
                : []);
            const staged = applyStageDurations(existing, today, durations);
            if (onUpdateDeliverableTimeline) {
              onUpdateDeliverableTimeline(c.id, d.id, staged);
            } else if (onChangeDeliverableTimeline) {
              onChangeDeliverableTimeline(deliverableKey, staged);
              onChangeDeliverableTimeline(d.id, staged);
            }
          });
        });
        // ToastService.success("Stage durations applied to all deliverables");
      }
    },
    [
      activeTimeline,
      creators,
      handleRangeEndChange,
      handleRangeStartChange,
      onChangeDeliverableTimeline,
      onApplyStageDurations,
      onUpdateDeliverableTimeline,
      rangeEnd,
    ]
  );

  const handleApplyPreset = useCallback(
    (preset: RangePreset) => {
      const { start, end } = preset.build();
      applyPreset(preset);
      setEditMode(true);

      if (onEqualizeTimelines) {
        onEqualizeTimelines(start, end);
      } else {
        creators.forEach((c) => {
          c.deliverables.forEach((d) => {
            const deliverableKey = `${c.id}_${d.id}`;
            const existing =
              activeTimeline?.[deliverableKey] ||
              activeTimeline?.[d.id] ||
              (d.stages && d.stages.length > 0
                ? d.stages
                : []);
            const staged = spreadStagesEvenly(existing, start, end);
            if (onUpdateDeliverableTimeline) {
              onUpdateDeliverableTimeline(c.id, d.id, staged);
            } else if (onChangeDeliverableTimeline) {
              onChangeDeliverableTimeline(deliverableKey, staged);
              onChangeDeliverableTimeline(d.id, staged);
            }
          });
        });
      }
    },
    [
      activeTimeline,
      applyPreset,
      creators,
      onChangeDeliverableTimeline,
      onEqualizeTimelines,
      onUpdateDeliverableTimeline,
    ]
  );

  const dataSource = useMemo((): GanttRowRecord[] => {
    return creators.map((creator) => {
      const creatorRecord = creator as unknown as Record<string, unknown>;
      const creatorRawStatus =
        typeof creatorRecord.status === "string"
          ? creatorRecord.status.toLowerCase()
          : "";
      const isCreatorCancelled = Boolean(
        creator.isCancelled ||
        creatorRawStatus.includes("cancel") ||
        creatorRawStatus.includes("decline") ||
        creatorRawStatus.includes("withdraw")
      );

      const childRows: GanttRowRecord[] = creator.deliverables.map((del) => {
        const deliverableKey = `${creator.id}_${del.id}`;
        const timeline =
          activeTimeline?.[deliverableKey] ||
          activeTimeline?.[del.id] ||
          del.stages ||
          activeTimeline?.[del.name] ||
          [];
        const isDeliverableCancelled = Boolean(
          isCreatorCancelled ||
          del.isCancelled ||
          del.status === "cancelled" ||
          del.status?.toLowerCase().includes("cancel") ||
          del.status?.toLowerCase().includes("decline") ||
          del.status?.toLowerCase().includes("withdraw")
        );
        const statuses = getStageStatuses(
          isDeliverableCancelled ? "cancelled" : del.status
        );
        const placed = placeStages(
          timeline,
          statuses,
          rangeStart,
          rangeEnd,
          isDeliverableCancelled
        );

        return {
          key: `del-${creator.id}-${del.id}`,
          rowType: "deliverable",
          creatorId: creator.id,
          deliverableId: del.id,
          deliverableName: del.name,
          deliverableStatus: isDeliverableCancelled ? "cancelled" : del.status,
          stages: placed,
          isCancelled: isDeliverableCancelled,
        };
      });

      return {
        key: `creator-${creator.id}`,
        rowType: "creator",
        creatorId: creator.id,
        creatorName: creator.name,
        influencerName: creator.influencerName || creator.name,
        creatorHandle: creator.handle,
        creatorImage: creator.image,
        creatorPlatform: creator.platform,
        deliverableCount: creator.deliverables.length,
        children: childRows.length > 0 ? childRows : undefined,
        isCancelled: isCreatorCancelled,
      };
    });
  }, [activeTimeline, creators, rangeEnd, rangeStart]);

  const toggleCollapse = useCallback((creatorId: string | number) => {
    setExpandedRowKeys((prev) => {
      const key = `creator-${creatorId}`;
      const exists = prev.includes(key);
      return exists ? prev.filter((k) => k !== key) : [...prev, key];
    });
  }, []);

  const columns: ColumnsType<GanttRowRecord> = useMemo(
    () =>
      buildGanttColumns({
        editMode,
        allCreatorsSelected,
        someCreatorsSelected,
        selectedCreatorIds,
        expandedRowKeys,
        ticks,
        todayPct,
        drag,
        selectAllCreators,
        clearCreatorSelection,
        toggleCreatorSelected,
        toggleCollapse,
        registerTrackRef,
        beginDrag,
        onOpenDeliverable,
      }),
    [
      allCreatorsSelected,
      beginDrag,
      clearCreatorSelection,
      drag,
      editMode,
      expandedRowKeys,
      onOpenDeliverable,
      registerTrackRef,
      selectAllCreators,
      selectedCreatorIds,
      someCreatorsSelected,
      ticks,
      todayPct,
      toggleCollapse,
      toggleCreatorSelected,
    ]
  );

  if (creators.length === 0) {
    return (
      <div className={`${styles.ganttContainer} ${className || ""}`}>
        <div className={styles.emptyState}>
          {emptyText || (
            <>
              <div className={styles.emptyTitle}>Nothing to chart</div>
              <div className={styles.emptySubtitle}>
                Add creators with deliverables to see progress.
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.ganttContainer} ${className || ""}`}>
      <GanttHeader
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        rangePresets={rangePresets}
        editMode={editMode}
        isSaving={isSaving}
        someCreatorsSelected={someCreatorsSelected}
        allCreatorsSelected={allCreatorsSelected}
        selectedCount={selectedCreatorIds.size}
        totalCreatorsCount={allCreatorIds.length}
        onToggleEditMode={toggleEditMode}
        onCancelEditMode={cancelEditMode}
        onSelectAllCreators={selectAllCreators}
        onClearCreatorSelection={clearCreatorSelection}
        onRangeStartChange={handleRangeStartChange}
        onRangeEndChange={handleRangeEndChange}
        onApplyPreset={handleApplyPreset}
        onApplyStageDurations={handleApplyStageDurations}
      />

      <Table<GanttRowRecord>
        className={styles.ganttTable}
        columns={columns}
        title={title ? () => title : undefined}
        dataSource={dataSource}
        pagination={false}
        virtual
        scroll={{ x: "max-content", y: scrollY }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
          showExpandColumn: false
        }}
        rowClassName={(record) =>
          record.rowType === "creator"
            ? styles.creatorRow
            : styles.deliverableRow
        }
      />
    </div>
  );
};
